import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  AmbassadorRegistrationForm,
  AmbassadorRegistrationFormValues,
} from './ambassadorRegistrationForm';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG } from '../../utils/constants';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ambassadorFormValuesToUpdateRequest } from '../../utils/ambassadors';
import { useUpdateAmbassador } from '../../hooks/ambassadors/updateAmbassador';
import { useUser } from '../../hooks/user';
import { useGetAmbassador } from '../../hooks/ambassadors/getAmbassador';
import { Link, Modal } from '@amzn/awsui-components-react';

interface UpdateAmbassadorModalProps {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  onSuccess: () => void;
  onError: () => void;
}

export function UpdateAmbassadorModal({
  visible,
  setVisible,
  onSuccess,
  onError,
}: UpdateAmbassadorModalProps) {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const [formValues, setFormValues] = useState<AmbassadorRegistrationFormValues>({
    country: null,
    city: null,
    siteType: null,
    siteCode: null,
    businessUnit: null,
    siteRepresentation: false,
    teamRepresentation: false,
    employeeLedGroupRepresentation: false,
    howDidYouHearSpecification: '',
    howDidYouHear: null,
    myManagerHasApprovedThisRole: false,
    sendMeACopy: false,
  });

  const user = useUser();
  const ambassadorDetails = useGetAmbassador(user.employeePersonId || '');

  const { t } = useTranslation(['ambassadors', 'translation']);
  const updateAmbassador = useUpdateAmbassador();

  useEffect(() => {
    if (ambassadorDetails) {
      const ambassadorData = ambassadorDetails.data?.ambassador;
      // if we have data and have not set initial formvalues yet.
      if (ambassadorData && !formValues.country && !formValues.city && !formValues.siteType) {
        setFormValues((fv) => ({
          ...fv,
          country: { label: ambassadorData.country, value: ambassadorData.country },
          city: { label: ambassadorData.city, value: ambassadorData.city },
          siteLead: ambassadorData.siteLead,
          siteType: { label: ambassadorData.siteType, value: ambassadorData.siteType },
          siteRepresentation: !!(
            ambassadorData.representationTypes &&
            ambassadorData.representationTypes.includes('SITE')
          ),
          teamRepresentation: !!(
            ambassadorData.representationTypes &&
            ambassadorData.representationTypes.includes('TEAM')
          ),
          employeeLedGroupRepresentation: !!(
            ambassadorData.representationTypes && ambassadorData.representationTypes.includes('ELG')
          ),
        }));
      }
    }
  }, [ambassadorDetails, setFormValues, formValues]);

  const submitUpdateAmbassador = async () => {
    updateAmbassador.mutate(
      ambassadorFormValuesToUpdateRequest(formValues, user.employeePersonId),
      {
        onSuccess: (data) => {
          setVisible(false);
          onSuccess();
        },
        onError: (err) => {
          console.error(err);

          setVisible(false);
          onError();
        },
      }
    );
  };

  if (featureIsEnabled(COMMUNITY_AMBASSADOR_FEATURE_FLAG, features, searchParams)) {
    return (
      <Modal
        onDismiss={() => setVisible(false)}
        visible={visible}
        header={t('ambassadorRegistration.updateRegistration')}
        footer={
          <>
            <p>
              {t('ambassadorRegistration.wantToLeave')}{' '}
              <Link href="/ambassadors/offboarding">
                {t('ambassadorRegistration.unsubscribeHere')}
              </Link>
            </p>
          </>
        }
      >
        <AmbassadorRegistrationForm
          formValues={formValues}
          setFormValues={setFormValues}
          onSubmit={submitUpdateAmbassador}
          loading={updateAmbassador.isPending}
          isUpdateForm
        />
      </Modal>
    );
  }
  return <></>;
}
